/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'

import { EuiFormRow, EuiButtonGroup } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { ResourceType } from '@modules/network-security-lib/types'

type Props = {
  value: ResourceType | null
  onChange: (value: ResourceType) => void
  isServerlessComingSoon: boolean
  isDisabled: boolean
}

const ResourceTypeField: React.FunctionComponent<Props> = ({
  value,
  onChange,
  isServerlessComingSoon,
  isDisabled,
}) => {
  const { formatMessage } = useIntl()

  useEffect(() => {
    if (!value && isServerlessComingSoon) {
      onChange('hosted')
    }
  }, [isServerlessComingSoon, value, onChange])

  const label = formatMessage({ id: 'resourceTypeField.label', defaultMessage: 'Resource type' })

  return (
    <EuiFormRow
      label={label}
      helpText={
        <FormattedMessage
          id='resourceTypeField.helpText'
          defaultMessage='This policy can be applied only to resources with the selected resource type.'
        />
      }
    >
      <EuiButtonGroup
        type='single'
        buttonSize='s'
        legend={label}
        options={[
          {
            id: 'hosted',
            label: formatMessage({
              id: 'resourceTypeField.hostedLabel',
              defaultMessage: 'Hosted deployment',
            }),
            isDisabled,
          },
          {
            id: 'serverless',
            label: formatMessage({
              id: 'resourceTypeField.serverlessLabel',
              defaultMessage: 'Serverless project',
            }),
            isDisabled: isServerlessComingSoon || isDisabled,
            toolTipContent: isServerlessComingSoon ? (
              <FormattedMessage
                id='resourceTypeField.serverlessComingSoon'
                defaultMessage='Coming soon'
              />
            ) : undefined,
          },
        ]}
        idSelected={value || ''}
        onChange={(optionId) => onChange(optionId as ResourceType)}
      />
    </EuiFormRow>
  )
}

export default ResourceTypeField
